import React from "react";
import { Text, Pressable, StyleSheet, ActivityIndicator } from "react-native";
import { APP_TOKENS as tokens } from "../../../styles/tokens";

type SecondaryButtonProps = {
  text: string;
  onPress: () => void;
  disabled?: boolean;
  fullWidth?: boolean;
  roundBorderRadius?: boolean;
  width?: number;
  icon?: React.ReactNode;
  loading?: boolean;
  style?: any;
  textStyle?: any;
  selected?: boolean;
  accessibilityLabel?: string;
};

const SecondaryButton: React.FC<SecondaryButtonProps> = ({
  text,
  onPress,
  icon = null,
  disabled = false,
  fullWidth = false,
  roundBorderRadius = false,
  width = -1,
  loading = false,
  style = null,
  textStyle = null,
  selected = false,
  accessibilityLabel,
}) => {
  const [isFocused, setIsFocused] = React.useState(false);

  if (selected) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel || text || "Button"}
        accessibilityState={{ selected: true, disabled: true }}
        {...({ "aria-pressed": true } as any)}
        style={[
          styles.button,
          isFocused && styles.focused,
          fullWidth && { width: "100%" },
          roundBorderRadius && { borderRadius: tokens.radius.round },
          width !== -1 && { width: width },
          { height: text ? 46 : 36 },
          {
            paddingVertical: text ? tokens.spacing.md : tokens.spacing.sm,
            paddingHorizontal: text ? tokens.spacing.xxl : tokens.spacing.sm,
          },
          text === "" && styles.iconOnly,
          { backgroundColor: tokens.color.accentMuted },
          style,
        ]}
        onPress={onPress}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled
      >
        {icon ? icon : null}
        {text && <Text style={[styles.text, textStyle]}>{text}</Text>}
      </Pressable>
    );
  }

  if (loading) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel || text || "Button"}
        accessibilityState={{ disabled: true, busy: true }}
        style={[
          styles.button,
          isFocused && styles.focused,
          fullWidth && { width: "100%" },
          roundBorderRadius && { borderRadius: tokens.radius.round },
          width !== -1 && { width: width },
          { height: text ? 46 : 36 },
          {
            paddingVertical: text ? tokens.spacing.md : tokens.spacing.sm,
            paddingHorizontal: text ? tokens.spacing.xxl : tokens.spacing.sm,
          },
          text === "" && styles.iconOnly,
          style,
        ]}
        onPress={onPress}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled
      >
        <ActivityIndicator size="small" color={tokens.color.textPrimary} />
      </Pressable>
    );
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel || text || "Button"}
      accessibilityState={{ disabled, selected }}
      {...({ "aria-pressed": selected } as any)}
      style={[
        styles.button,
        isFocused && styles.focused,
        disabled && styles.disabledButton,
        fullWidth && { width: "100%" },
        width !== -1 && { width: width },
        roundBorderRadius && { borderRadius: tokens.radius.round },
        { height: text ? 46 : 36 },
        {
          paddingVertical: text ? tokens.spacing.md : tokens.spacing.sm,
          paddingHorizontal: text ? tokens.spacing.xxl : tokens.spacing.sm,
        },
        text === "" && styles.iconOnly,
        style,
      ]}
      onPress={onPress}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      disabled={disabled}
    >
      {icon ? icon : null}
      {text && <Text style={[styles.text, textStyle]}>{text}</Text>}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  button: {
    backgroundColor: tokens.color.surface,
    paddingVertical: tokens.spacing.md,
    paddingRight: tokens.spacing.xxl,
    borderRadius: tokens.radius.button,
    height: 46,
    borderColor: tokens.color.textPrimary,
    borderWidth: tokens.borderWidth.thin,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: tokens.spacing.md,
  },
  text: {
    fontFamily: tokens.font.medium,
    textAlign: "center",
    fontSize: tokens.fontSize.md,
    lineHeight: tokens.lineHeight.md,
    letterSpacing: 0.032,
    fontStyle: "normal",
    color: tokens.color.textPrimary,
    userSelect: "none",
  },
  focused: {
    outlineColor: tokens.color.accentDark,
    outlineOffset: tokens.spacing.xs,
    outlineStyle: "solid",
    outlineWidth: tokens.borderWidth.strong,
  } as any,
  disabledButton: {
    backgroundColor: tokens.color.borderStrong,
    opacity: tokens.opacity.disabled,
  },
  iconOnly: {
    width: tokens.spacing.huge + tokens.spacing.xs,
    minWidth: tokens.spacing.huge + tokens.spacing.xs,
    minHeight: tokens.spacing.huge + tokens.spacing.xs,
    height: tokens.spacing.huge + tokens.spacing.xs,
  },
  disabledText: {
    color: tokens.color.inverse,
  },
});

export default SecondaryButton;
